/**
 * Multi-Clinic User Manager Frontend API
 * Provides frontend interface to multi-clinic user operations
 */

import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { apiClient } from './apiClient';

class MultiClinicUserManager {
  constructor() {
    // No configuration needed - apiClient handles everything
  }

  // No custom headers needed when Amplify REST API uses Cognito authorizer

  /**
   * Get current user's Cognito sub
   */
  async getCurrentUserSub() {
    try {
      const user = await getCurrentUser();
      if (!user || !user.userId) {
        throw new Error('User object or sub not found');
      }
      return user.userId;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw new Error('User not authenticated');
    }
  }

  /**
   * Get user's multi-clinic data
   */
  async getUserMultiClinicData(cognitoSub = null) {
    try {
      const userSub = cognitoSub || await this.getCurrentUserSub();
      
      const data = await apiClient.multiClinic({ operation: 'getUserMultiClinicData', cognitoSub: userSub });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to get user data');
      }

      return data.data;
    } catch (error) {
      console.error('Error getting user multi-clinic data:', error);
      throw error;
    }
  }

  /**
   * Switch user's active clinic
   */
  async switchUserClinic(clinicId, cognitoSub = null) {
    try {
      const userSub = cognitoSub || await this.getCurrentUserSub();
      
      const data = await apiClient.multiClinic({ operation: 'switchUserClinic', cognitoSub: userSub, clinicId });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to switch clinic');
      }

      // Refresh session to get updated JWT claims (best-effort)
      try {
        await fetchAuthSession({ forceRefresh: true });
        console.log('Session refreshed after clinic switch');
      } catch (refreshError) {
        console.warn('Could not refresh session:', refreshError);
      }

      return data.data;
    } catch (error) {
      console.error('Error switching user clinic:', error);
      throw error;
    }
  }

  /**
   * Get user's topics for a specific clinic
   */
  async getUserTopicsForClinic(clinicId, cognitoSub = null) {
    try {
      const userSub = cognitoSub || await this.getCurrentUserSub();
      
      const data = await apiClient.multiClinic({ operation: 'getUserTopicsForClinic', cognitoSub: userSub, clinicId });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to get topics');
      }

      return data.data;
    } catch (error) {
      console.error('Error getting user topics:', error);
      throw error;
    }
  }

  /**
   * Get user's permissions across all clinics
   */
  async getUserPermissions(cognitoSub = null) {
    try {
      const userSub = cognitoSub || await this.getCurrentUserSub();
      
      const data = await apiClient.multiClinic({ operation: 'getUserPermissions', cognitoSub: userSub });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to get permissions');
      }

      return data.data;
    } catch (error) {
      console.error('Error getting user permissions:', error);
      throw error;
    }
  }

  /**
   * Update user's role in a specific clinic (admin operation)
   */
  async updateUserRoleInClinic(targetUserSub, clinicId, newRole, adminCognitoSub = null) {
    try {
      const adminSub = adminCognitoSub || await this.getCurrentUserSub();

      const data = await apiClient.multiClinic({ operation: 'updateUserRoleInClinic', cognitoSub: adminSub, targetUserSub, clinicId, role: newRole });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to update user role');
      }

      return data.data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Add user to clinic (admin operation)
   */
  async addUserToClinic(targetUserSub, clinicId, role, topics = [], adminCognitoSub = null) {
    try {
      const adminSub = adminCognitoSub || await this.getCurrentUserSub();

      const data = await apiClient.multiClinic({
        operation: 'addUserToClinic',
        cognitoSub: adminSub,
        targetUserSub,
        clinicId,
        role,
        topics
      });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to add user to clinic');
      }

      return data.data;
    } catch (error) {
      console.error('Error adding user to clinic:', error);
      throw error;
    }
  }

  /**
   * Update user's topics for a specific clinic (admin operation)
   */
  async updateUserTopicsForClinic(targetUserSub, clinicId, topics, adminCognitoSub = null) {
    try {
      const adminSub = adminCognitoSub || await this.getCurrentUserSub();

      const data = await apiClient.multiClinic({
        operation: 'updateUserTopicsForClinic',
        cognitoSub: adminSub,
        targetUserSub,
        clinicId,
        topics
      });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to update user topics');
      }

      return data.data;
    } catch (error) {
      console.error('Error updating user topics:', error);
      throw error;
    }
  }

  /**
   * Remove user from clinic (admin operation)
   */
  async removeUserFromClinic(targetUserSub, clinicId, adminCognitoSub = null) {
    try {
      const adminSub = adminCognitoSub || await this.getCurrentUserSub();

      const data = await apiClient.multiClinic({ operation: 'removeUserFromClinic', cognitoSub: adminSub, targetUserSub, clinicId });

      if (!data || data.success !== true) {
        throw new Error((data && data.error) || 'Failed to remove user from clinic');
      }

      return data.data;
    } catch (error) {
      console.error('Error removing user from clinic:', error);
      throw error;
    }
  }

  /**
   * Parse user's multi-clinic data from JWT claims
   */
  parseJWTClaims(user) {
    try {
      const attributes = user.attributes || user.signInUserSession?.idToken?.payload || {};
      
      // Check if user has multi-clinic data
      const isMultiClinic = attributes['custom:is_multi_clinic'] === 'true';
      
      if (!isMultiClinic) {
        // Legacy single-clinic user
        return {
          isMultiClinic: false,
          defaultClinic: attributes['custom:clinic_id'] || attributes['family_name'],
          role: attributes['custom:role'] || attributes['preferred_username'],
          topics: (attributes['custom:topics'] || '').split(',').filter(t => t.trim()),
          clinics: [{
            clinicId: attributes['custom:clinic_id'] || attributes['family_name'],
            role: attributes['custom:role'] || attributes['preferred_username'],
            topics: (attributes['custom:topics'] || '').split(',').filter(t => t.trim())
          }],
          permissions: {
            isAdmin: attributes['custom:is_admin'] === 'true',
            isSuperAdmin: attributes['custom:is_super_admin'] === 'true',
            canManageUsers: attributes['custom:is_admin'] === 'true',
            totalClinics: 1
          }
        };
      }

      // Multi-clinic user
      const allClinics = JSON.parse(attributes['custom:all_clinics'] || '[]');
      const allClinicIds = (attributes['custom:all_clinic_ids'] || '').split(',').filter(id => id.trim());
      const allTopics = (attributes['custom:topics'] || '').split(',').filter(t => t.trim());
      
      return {
        isMultiClinic: true,
        defaultClinic: attributes['custom:default_clinic'],
        role: attributes['custom:role'], // Role in default clinic
        topics: allTopics,
        clinics: allClinics,
        allClinicIds,
        permissions: {
          isAdmin: attributes['custom:is_admin'] === 'true',
          isSuperAdmin: attributes['custom:is_super_admin'] === 'true',
          canManageUsers: attributes['custom:is_admin'] === 'true',
          totalClinics: parseInt(attributes['custom:clinic_count'] || '0', 10),
          highestRole: attributes['custom:highest_role'] || 'viewer'
        }
      };
    } catch (error) {
      console.error('Error parsing JWT claims:', error);
      return {
        isMultiClinic: false,
        defaultClinic: 'unknown',
        role: 'viewer',
        topics: [],
        clinics: [],
        permissions: {
          isAdmin: false,
          isSuperAdmin: false,
          canManageUsers: false,
          totalClinics: 0
        }
      };
    }
  }

  /**
   * Get clinic context for current user
   */
  async getClinicContext(user) {
    try {
      const jwtData = this.parseJWTClaims(user);
      
      if (jwtData.isMultiClinic) {
        // Get fresh data from database for multi-clinic users
        const freshData = await this.getUserMultiClinicData();
        return {
          ...jwtData,
          clinics: freshData.clinics,
          availableClinics: freshData.clinics.map(clinic => ({
            id: clinic.clinicId,
            name: clinic.clinicName || clinic.clinicId, // Use clinicName from backend, fallback to ID
            role: clinic.role,
            topics: clinic.topics
          }))
        };
      }

      return {
        ...jwtData,
        availableClinics: [{
          id: jwtData.defaultClinic,
          name: jwtData.defaultClinic,
          role: jwtData.role,
          topics: jwtData.topics
        }]
      };
    } catch (error) {
      console.error('Error getting clinic context:', error);
      throw error;
    }
  }

  /** Get current user's basic info */
  async getCurrentUserInfo() {
    try {
      const user = await getCurrentUser();
      const { fetchUserAttributes } = await import('@aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      return {
        cognitoSub: user.userId,
        email: attributes.email,
        name: attributes.given_name || attributes.email
      };
    } catch (error) {
      throw new Error('User not authenticated');
    }
  }
}

// Export singleton instance
export const multiClinicUserManager = new MultiClinicUserManager();
export default multiClinicUserManager;
